/**
 * The Stats screen's words and numbers, as plain functions (tested in model.test.ts):
 * the streak's line, the chart's scale and summary, dates, the challenge tally. The
 * history itself (records, trend, calendar) is the engine's (`history.ts`). No React,
 * no storage, no clock.
 */
import { type DateKey, type DayCell, formatDuration, type LevelRecord, type Solve, type StreakStatus, type Trend } from '@arrow/game-engine';
import { type Level, LEVELS } from '@arrow/puzzle-schema';

import type { ChallengeResults } from '../../api/client.ts';
import { LEVEL_NAME } from '../today-model.ts';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);

/** "23 October" (or "23 October 2025" when it isn't `thisYear`). */
export function longDate(key: DateKey, thisYear?: number): string {
	const [y, m, d] = key.split('-').map(Number) as [number, number, number];
	return `${d}\u00a0${MONTHS[m - 1]}${thisYear !== undefined && y !== thisYear ? ` ${y}` : ''}`;
}

/** "23 Oct". */
export function shortDate(key: DateKey): string {
	const [, m, d] = key.split('-').map(Number) as [number, number, number];
	return `${d}\u00a0${MONTHS[m - 1]!.slice(0, 3)}`;
}

/** "October 2026". */
export const monthName = (year: number, month: number) => `${MONTHS[month - 1]} ${year}`;
export const weekdayName = (i: number) => WEEKDAYS[i]!;
/** One letter per weekday, Monday first. */
export const WEEKDAY_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const;

export const yearOf = (key: DateKey) => Number(key.slice(0, 4));

/**
 * The streak's line under its numeral, or nothing: once today's in, the label beside the
 * numeral says so ("days in a row · today's in"), and the line is only for a new best.
 * Never pressure: no countdowns, no "you'll lose it".
 */
export function streakLine(s: StreakStatus): string | null {
	// Plain facts; the link after it ("Play today’s Sparrow") is the call to action (James, 2 Nov).
	if (s.current === 0) return s.best > 0 ? `Your best was ${s.best} ${plural(s.best, 'day')}.` : null;
	if (!s.todayCounted) return 'Not played today yet.';
	if (s.isBest) return 'Your longest streak yet.';
	return null;
}

/** The level the panel opens on: the last one you played if it has solves, else your most-solved, else Medium. */
export function defaultLevel(records: Record<Level, LevelRecord>, remembered: Level | null): Level {
	if (remembered && records[remembered].solved > 0) return remembered;
	let best: Level = 'medium';
	for (const l of LEVELS) if (records[l].solved > records[best].solved) best = l;
	return best;
}

/** The foot's word on help: how many of your dailies went without a check or a reveal. */
export function helpLine(perfect: number, solved: number): string {
	if (perfect >= solved) return 'Every daily without a check or a reveal.';
	return `${perfect} of the ${solved} dailies without a check or a reveal.`;
}

/** "0:32 quicker", "0:12 slower", "about the same" (within 3 seconds). */
export function trendWords(t: Trend): { words: string; dir: 'quicker' | 'slower' | 'same' } {
	if (Math.abs(t.deltaMs) < 3000) return { words: 'About the same', dir: 'same' };
	const by = formatDuration(Math.abs(t.deltaMs));
	return t.deltaMs < 0 ? { words: `${by} quicker`, dir: 'quicker' } : { words: `${by} slower`, dir: 'slower' };
}

// ---- The times chart -----------------------------------------------------------------

/** The most solves the chart draws (the latest ones). */
export const CHART_MAX = 30;

const STEPS_S = [15, 30, 60, 120, 300, 600, 900, 1800, 3600];

/** The chart's top and gridlines: a round top just over the slowest time, two or three lines. */
export function timeScale(maxMs: number): { topMs: number; ticksMs: number[] } {
	const max = Math.max(1, maxMs / 1000);
	const step = STEPS_S.find((s) => max / s <= 3) ?? Math.ceil(max / 3 / 3600) * 3600;
	const top = Math.ceil((max * 1.04) / step) * step;
	const ticks: number[] = [];
	for (let t = step; t <= top; t += step) ticks.push(t * 1000);
	return { topMs: top * 1000, ticksMs: ticks };
}

/** An axis label: "2:00", "10:00", "1:00:00". */
export const tickLabel = (ms: number) => formatDuration(ms);

/** How far over your typical time the chart reaches before a bar runs off its top. */
export const CHART_CAP = 2.5;

/**
 * The chart's scale for these times: one long solve (a puzzle left open overnight) mustn't
 * flatten the rest to a hairline, so the scale stops at about 2.5× your typical time and
 * anything slower runs off the top (`capped`), its real time left to the words.
 */
export function chartScale(timesMs: readonly number[], typicalMs: number | null): { topMs: number; ticksMs: number[]; capped: boolean } {
	const max = Math.max(1, ...timesMs, typicalMs ?? 0);
	const cap = typicalMs && typicalMs > 0 ? typicalMs * CHART_CAP : Infinity;
	const capped = max > cap;
	return { ...timeScale(capped ? cap : max), capped };
}

/** The chart in words, for screen readers and as its caption. */
export function chartSummary(level: Level, solves: readonly Solve[], record: LevelRecord): string {
	const n = solves.length;
	if (n === 0) return '';
	const name = LEVEL_NAME[level];
	const times = solves.map((s) => s.elapsedMs);
	const parts = [
		`Your last ${n} ${name} ${plural(n, 'time')}, oldest first, from ${shortDate(solves[0]!.dateKey)} to ${shortDate(solves[n - 1]!.dateKey)}.`,
		`Quickest ${formatDuration(Math.min(...times))}, slowest ${formatDuration(Math.max(...times))}.`,
	];
	if (record.bestMs !== null && record.bestOn) parts.push(`Your best is ${formatDuration(record.bestMs)}, on ${longDate(record.bestOn)}.`);
	const revealed = solves.filter((s) => s.revealsUsed > 0).length;
	if (revealed) parts.push(`${revealed} ${plural(revealed, 'solve')} used a reveal.`);
	const { topMs } = chartScale(times, record.medianMs);
	const over = times.filter((t) => t > topMs).length;
	if (over) parts.push(`${over} ${plural(over, 'time')} ${over === 1 ? 'runs' : 'run'} off the top of the chart.`);
	if (record.trend) parts.push(`Your last ${record.trend.window}: ${trendWords(record.trend).words.toLowerCase()} than the ${record.trend.window} before.`);
	return parts.join(' ');
}

/** "2 checks, 1 reveal", or "Perfect". */
export function helpWords(s: Pick<Solve, 'checksUsed' | 'revealsUsed'>): string {
	const parts = [
		s.checksUsed ? `${s.checksUsed} ${plural(s.checksUsed, 'check')}` : null,
		s.revealsUsed ? `${s.revealsUsed} ${plural(s.revealsUsed, 'reveal')}` : null,
	].filter((x): x is string => x !== null);
	return parts.length ? parts.join(', ') : 'Perfect';
}

// ---- Challenges ------------------------------------------------------------------------

/** The most challenges whose results Stats asks for (the latest). */
export const CHALLENGES_ASKED = 10;

export interface ChallengeTally {
	/** Challenges whose results came back. */
	counted: number;
	played: number;
	finished: number;
	/** Friends who beat your time. */
	beat: number;
	/** Finished without beating it. */
	heldOff: number;
}

/** Adds up the results that came back (failures are left out, never guessed). */
export function tallyChallenges(results: ReadonlyArray<ChallengeResults | null>): ChallengeTally {
	const ok = results.filter((r): r is ChallengeResults => r !== null);
	const played = ok.reduce((n, r) => n + r.played, 0);
	const finished = ok.reduce((n, r) => n + r.finished, 0);
	const beat = ok.reduce((n, r) => n + r.beat, 0);
	return { counted: ok.length, played, finished, beat, heldOff: Math.max(0, finished - beat) };
}

/** The challenges you took, in a line: "You've beaten 3 of 5 challengers." Anonymous. */
export function takenLine(t: { taken: number; beat: number }): string {
	if (t.taken === 0) return 'Take a friend’s challenge and it counts here.';
	if (t.beat === 0) return `${t.taken === 1 ? 'Your challenger' : `All ${t.taken} challengers`} held you off. Next time.`;
	if (t.beat === t.taken) return t.taken === 1 ? 'You beat your challenger.' : `You’ve beaten all ${t.taken} challengers.`;
	return `You’ve beaten ${t.beat} of ${t.taken} ${plural(t.taken, 'challenger')}.`;
}

/** The percentile line, only for a number the api gave (≥ 1 and honest). */
export function percentileLine(level: Level, ms: number, percentile: number | null): string | null {
	if (typeof percentile !== 'number' || !Number.isFinite(percentile) || percentile < 1) return null;
	const p = Math.floor(Math.min(100, percentile));
	return `Today’s ${formatDuration(ms)} was faster than ${p}% of ${LEVEL_NAME[level]} solvers.`;
}

// ---- Calendar ----------------------------------------------------------------------------

/** A day in words: "Friday 23 October, today: Easy and Medium solved, Hard caught up later." */
export function dayWords(cell: DayCell, thisYear?: number, before = false): string {
	const when = `${weekdayName(cell.weekday)} ${longDate(cell.dateKey, thisYear)}${cell.isToday ? ', today' : ''}`;
	if (cell.isFuture) return `${when}: still to come.`;
	// Drawn blank: a day before your first Sparrow isn't a day you missed (QA2-14).
	if (before) return `${when}: before your first Sparrow.`;
	const by = (mark: DayCell['marks'][Level]) => LEVELS.filter((l) => cell.marks[l] === mark).map((l) => LEVEL_NAME[l]);
	const join = (xs: string[]) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}`);
	const parts = [
		by('solved').length ? `${join(by('solved'))} solved` : null,
		by('late').length ? `${join(by('late'))} solved later` : null,
		by('started').length ? `${join(by('started'))} started` : null,
	].filter((x): x is string => x !== null);
	return `${when}: ${parts.length ? parts.join(', ') : 'not played'}.`;
}
